/**
 * Canonical registry for every HTTP header the Hero platform sends
 * or reads. New headers MUST land here first — pick up the value
 * from `CONFIG.headers.<name>` in the SDK / backend / dashboards
 * rather than re-declaring the string. That way "which product /
 * OS / timezone is calling?" has exactly one source of truth and
 * grepping this file tells you the entire header vocabulary.
 *
 * See `docs/ai/user-signals.md` for the semantics of the
 * `x-hero-app` + `x-hero-platform` pair.
 */
export const headers = {
  /**
   * Bearer token for authenticated requests. The SDK builds this
   * from the caller's access token / temp token / phone token —
   * see `libs/shared/sdks/core-sdk/src/client/http-client.ts`.
   */
  authorization: 'Authorization',
  /** the header for the user target language */
  language: 'accept-language',
  /**
   * Current-user coordinates in the format `"lat,lng"`. Sent by
   * every SDK on every request from whatever the client's most
   * recent geolocation read produced.
   *
   * Paired with {@link currentUserLocationCapturedAt} +
   * {@link currentUserLocationAccuracyMeters} so the backend can
   * reason about freshness and fidelity, not just the raw coords.
   * The `PresenceInterceptor`
   * (`apps/backend/src/app/interceptor/presence.interceptor.ts`)
   * uses that trio to fan-out to
   * `User.meta.location.accurate` / `Driver.meta.lastKnownLocation`
   * / `Vehicle.meta.lastKnownLocation` on every authed request from
   * a mobile driver — see `docs/ai/user-signals.md`
   * "Passive location fan-out".
   */
  currentUserLocation: 'user-location',
  /**
   * Epoch-ms timestamp for when the client actually captured the
   * coords in {@link currentUserLocation}. Lets the backend drop
   * stale reads (e.g. mobile module-scope cache from 10 minutes
   * ago after the app spent time offline) instead of writing them
   * as if they were live.
   *
   * Optional — legacy SDKs without this header still get their
   * location honoured for the per-request `ContextService.currentLocation`
   * pipeline (used by `order-action.service.ts` when a driver
   * marks a milestone), but the fan-out to
   * `User.meta.location.accurate` is skipped because we can't tell
   * how stale the read is.
   */
  currentUserLocationCapturedAt: 'x-hero-location-captured-at',
  /**
   * GPS accuracy radius in metres for the reading in
   * {@link currentUserLocation}. Lets the backend split
   * "GPS-quality accurate write" from "browser IP-geolocation dressed
   * up as GPS" — the interceptor rejects reads coarser than
   * `accuracyMetersMax` (mobile: 200m, web: 5000m) before writing
   * to the meta fan-out.
   *
   * Optional. See {@link currentUserLocationCapturedAt}.
   */
  currentUserLocationAccuracyMeters: 'x-hero-location-accuracy-meters',
  /**
   * IANA timezone of the calling device, sent by every SDK from
   * `Intl.DateTimeFormat().resolvedOptions().timeZone` (web) and
   * `Localize.getTimeZone()` / Intl on mobile. The backend's `Tz`
   * param decorator (`apps/backend/src/app/decorator/timezone.decorator.ts`)
   * reads this header so date defaults (pickup window, agent
   * confirmation prose, lead analytics) match the caller's wall-clock
   * instead of falling back to `CONFIG.time.defaultTimezone` (`Asia/Riyadh`).
   */
  timezone: 'x-timezone',
  /**
   * Which Hero surface is calling — `carrier_app` / `customer_app`
   * / `dashboard` / `admin_panel`. Attached by every SDK on every
   * request (constant per SDK instance) and read by the backend
   * `PresenceInterceptor` to bump
   * `User.meta.presence.apps[(app, platform)].lastSeenAt` on every
   * authed request. Same vocabulary as
   * `HeroAppId` (`libs/shared/schema/src/common/app-registry.schema.ts`).
   *
   * See `docs/ai/user-signals.md` "Gateway convention" for the
   * paired-header pattern with `x-hero-platform`.
   */
  app: 'x-hero-app',
  /**
   * Which platform this SDK instance runs on — `ios` / `android` /
   * `web` / `unknown`. Same enum the auth layer already stores on
   * `RefreshSession.device.platform`, so a single vocabulary spans
   * auth / session / presence.
   *
   * Attached by every SDK on every request. The backend
   * `PresenceInterceptor` pairs it with `x-hero-app` to key
   * `User.meta.presence.apps[i]`. See `docs/ai/user-signals.md`.
   */
  platform: 'x-hero-platform',
} as const;
